import { useMemo } from "react";

import { Separator } from "@/components/ui/separator";

import type { GameType } from "../types";
import {
  AgeIcon,
  GoodForKidsIcon,
  HighTechIcon,
  LowTechIcon,
  PrivacyIcon,
} from "./icons";

interface FeatureItemProps {
  description?: string;
  Icon: React.FC<React.ComponentProps<"svg">>;
  title: string;
}

function FeatureItem({ description, Icon, title }: FeatureItemProps) {
  return (
    <li className="flex gap-2.5">
      <Icon />
      <p className="font-medium text-16/[1.25em] text-white">
        {title}
        {!!description && (
          <small className="mt-0.5 block text-14/[1.4em] text-cool-gray">
            {description}
          </small>
        )}
      </p>
    </li>
  );
}

interface FeaturesProps {
  className?: string;
  game: GameType;
}

export default function Features({ className, game }: FeaturesProps) {
  const items = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (game.minimumAge) {
      items.push(
        <FeatureItem
          description={game.minimumAge}
          Icon={AgeIcon}
          key="minimumAge"
          title="Age"
        />
      );
    }
    if (game.isHighTech) {
      items.push(
        <FeatureItem Icon={HighTechIcon} key="isHighTech" title="High tech" />
      );
    }
    if (game.isGoodForKids) {
      items.push(
        <FeatureItem
          Icon={GoodForKidsIcon}
          key="isGoodForKids"
          title="Good for kids"
        />
      );
    }
    if (game.isLowTech) {
      items.push(
        <FeatureItem Icon={LowTechIcon} key="isLowTech" title="Low tech" />
      );
    }
    if (game.privacy) {
      items.push(
        <FeatureItem Icon={PrivacyIcon} key="privacy" title={game.privacy} />
      );
    }

    return items;
  }, [game]);

  if (!items.length) {
    return null;
  }

  return (
    <div className={className}>
      <Separator className="mb-5 bg-white/20 md:hidden" />
      <h3 className="mb-4 font-semibold text-24 text-white md:hidden">
        Experience details
      </h3>
      <ul className="flex flex-col gap-2.5">{items}</ul>
      <Separator className="mt-5 bg-white/20 md:hidden" />
    </div>
  );
}
